import { LitElement, html } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { pb } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { invalidateNavCounts } from "../lib/nav-counts";
import type { Product, DppStatus } from "../lib/types";

// PassportState is a product's current passport status: the status of its most
// recent DPP version, or "none" if it has no version yet.
type PassportState = DppStatus | "none";

@customElement("products-page")
export class ProductsPage extends LitElement {
  static styles = [sharedStyles];

  @state() private products: Product[] = [];
  @state() private statusByProduct: Record<string, PassportState> = {};
  @state() private filter = "";
  @state() private loading = true;
  @state() private error = "";

  @state() private newName = "";
  @state() private newSku = "";
  @state() private creating = false;
  @state() private createError = "";

  @query("#create-dialog") private dialog?: MdDialog;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  protected firstUpdated(): void {
    // One-shot intent set by the dashboard's "New product" button.
    if (sessionStorage.getItem("dpp:create-product")) {
      sessionStorage.removeItem("dpp:create-product");
      this.openCreate();
    }
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      // Products come from the catalog API; each product's passport status is
      // derived from its dpp_versions records (org-scoped by the collection's
      // list rule). A product can hold several versions (e.g. a live published
      // one plus a new correction draft), so we pick by precedence —
      // published > draft > deactivated — to answer "what is this product's
      // passport state right now".
      const [products, versions] = await Promise.all([
        api.get<Product[]>("/catalog/products"),
        pb.collection("dpp_versions").getFullList<{
          product: string;
          status: DppStatus;
        }>({ requestKey: "products-passport-status" }),
      ]);
      const rank: Record<DppStatus, number> = {
        published: 3,
        draft: 2,
        deactivated: 1,
      };
      const statusByProduct: Record<string, PassportState> = {};
      for (const v of versions) {
        const current = statusByProduct[v.product];
        if (
          current === undefined ||
          rank[v.status] > rank[current as DppStatus]
        ) {
          statusByProduct[v.product] = v.status;
        }
      }
      this.products = products;
      this.statusByProduct = statusByProduct;
    } catch (err) {
      this.error = errorMessage(err, "Failed to load products.");
    } finally {
      this.loading = false;
    }
  }

  private passportState(productID: string): PassportState {
    return this.statusByProduct[productID] ?? "none";
  }

  private get filteredProducts(): Product[] {
    const q = this.filter.trim().toLowerCase();
    if (!q) return this.products;
    return this.products.filter((p) =>
      `${p.Name} ${p.SKU} ${this.passportState(p.ID)}`
        .toLowerCase()
        .includes(q),
    );
  }

  // formatDate renders a date, or "—" for the zero value. (Base collections in
  // this PocketBase version carry no created/updated autodate fields, so the
  // API returns 0001-01-01 — don't show that.)
  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  private passportBadge(state: PassportState) {
    switch (state) {
      case "published":
        return html`<span class="badge badge--ok"
          >${icon("verified")} Published</span
        >`;
      case "draft":
        return html`<span class="badge badge--neutral"
          >${icon("edit")} Draft</span
        >`;
      case "deactivated":
        return html`<span class="badge badge--bad"
          >${icon("flag")} Deactivated</span
        >`;
      default:
        return html`<span class="badge badge--neutral"
          >${icon("hourglass_empty")} No passport</span
        >`;
    }
  }

  private openCreate(): void {
    this.newName = "";
    this.newSku = "";
    this.createError = "";
    this.dialog?.show();
  }

  private async handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    this.createError = "";
    this.creating = true;
    try {
      const product = await api.post<Product>("/catalog/products", {
        name: this.newName,
        sku: this.newSku,
      });
      this.products = [...this.products, product];
      this.dialog?.close();
      invalidateNavCounts();
      toast(`Created product "${product.Name}"`);
      await navigate(`/products/${product.ID}`);
    } catch (err) {
      this.createError = errorMessage(err, "Failed to create product.");
    } finally {
      this.creating = false;
    }
  }

  render() {
    const list = this.filteredProducts;
    return html`
      <app-shell active="/products">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Products</h1>
              <p>
                Each product anchors its Digital Product Passport, identifier
                and supporting documents.
              </p>
            </div>
            <md-filled-button @click=${() => this.openCreate()}>
              New product
              <span slot="icon">${icon("add")}</span>
            </md-filled-button>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.products.length === 0
                ? html`<div class="card card--flush empty">
                    No products yet. Create your first product to get started.
                  </div>`
                : html`
                    <div class="card card--flush">
                      <div class="cardhead">
                        <h2>
                          All products
                          <small>· ${list.length} of ${this.products.length}</small>
                        </h2>
                        <md-outlined-text-field
                          label="Filter"
                          type="search"
                          .value=${this.filter}
                          @input=${(e: Event) => (this.filter = inputValue(e))}
                        >
                          <span slot="leading-icon">${icon("search")}</span>
                        </md-outlined-text-field>
                      </div>
                      <div class="table-scroll"><table>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Passport</th>
                            <th>Created</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          ${list.length === 0
                            ? html`<tr>
                                <td colspan="4" class="empty">
                                  No products match “${this.filter}”
                                </td>
                              </tr>`
                            : list.map(
                                (p) => html`
                                  <tr>
                                    <td>
                                      <a href="/products/${p.ID}"
                                        ><b>${p.Name}</b></a
                                      >
                                      ${p.SKU
                                        ? html`<small>${p.SKU}</small>`
                                        : null}
                                    </td>
                                    <td>
                                      ${this.passportBadge(
                                        this.passportState(p.ID),
                                      )}
                                    </td>
                                    <td>${this.formatDate(p.CreatedAt)}</td>
                                    <td>
                                      <a href="/products/${p.ID}">View →</a>
                                    </td>
                                  </tr>
                                `,
                              )}
                        </tbody>
                      </table></div>
                    </div>
                  `}
        </div>

        <md-dialog id="create-dialog">
          <div slot="headline">New product</div>
          <form
            slot="content"
            id="create-product-form"
            class="dialog-form"
            style="display:flex;flex-direction:column;gap:16px;padding-top:8px;min-width:min(360px,80vw)"
            @submit=${(e: Event) => void this.handleCreate(e)}
          >
            <md-outlined-text-field
              label="Name"
              required
              .value=${this.newName}
              @input=${(e: Event) => (this.newName = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="SKU (optional)"
              .value=${this.newSku}
              @input=${(e: Event) => (this.newSku = inputValue(e))}
            ></md-outlined-text-field>
            ${this.createError
              ? html`<p class="error-text">${this.createError}</p>`
              : null}
          </form>
          <div slot="actions">
            <md-text-button @click=${() => this.dialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              form="create-product-form"
              type="submit"
              ?disabled=${this.creating}
            >
              ${this.creating ? "Creating…" : "Create"}
            </md-filled-button>
          </div>
        </md-dialog>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "products-page": ProductsPage;
  }
}
